import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Badge, Checkbox, cn, Input, Label, LoadMoreFooter, SegmentedControl, Select, formatCount, useDebouncedValue, SearchableSelect, } from '@wit-support/ui';
import { formatDurationSec } from '@wit-support/ui-domain';
import { Images } from 'lucide-react';
import { listMediaSources } from '../api.js';
import { MEDIA_KEY } from '../shared.js';
import { ADS_QUERY_DEFAULTS, REFERENCE_STALE_MS } from '../query-policy.js';
import { MediaPreview } from './media-preview.js';
import { PickerResults } from './picker-parts.js';
/*
 * Один выбор медиа (спека v3 §2): «Загруженные» (наша медиатека), «Уже в
 * рекламе» (файлы объявлений кабинета — с текстами и ссылкой креатива),
 * «Библиотека кабинета» (файлы кабинета Meta, ещё не бывшие в рекламе).
 * Один вид элемента для всех трёх — `AdsMediaSourceItem`; в пачку он уходит
 * как `{assetId, origin?}`, перенос в кабинет запуска — автоматический.
 *
 * Им пользуются диалог выбора в форме запуска (`MediaPickerDialog`) и
 * страница «Медиатека» (вкладки «Уже в рекламе» и «Библиотека кабинета»).
 */
export const MEDIA_SOURCE_LABEL_RU = {
    uploaded: 'Загруженные',
    'in-ads': 'Уже в рекламе',
    'account-library': 'Библиотека кабинета',
};
export const MEDIA_SOURCES = ['uploaded', 'in-ads', 'account-library'];
export const MEDIA_SOURCE_PAGE = 30;
const ALL = '__all__';
/** Вкладки источников. */
export function MediaSourceTabs({ value, onChange, sources = MEDIA_SOURCES, }) {
    return (_jsx(SegmentedControl, { label: "\u041E\u0442\u043A\u0443\u0434\u0430 \u0444\u0430\u0439\u043B", value: value, onChange: onChange, options: sources.map((s) => ({ value: s, label: MEDIA_SOURCE_LABEL_RU[s] })) }));
}
export function useMediaFilters(defaultAccount) {
    const [account, setAccount] = useState(null);
    const [q, setQ] = useState('');
    const [kind, setKind] = useState(null);
    return {
        value: { account: account ?? defaultAccount, q, kind },
        setAccount,
        setQ,
        setKind,
    };
}
export function MediaFilters({ filters, accounts, withAccount, }) {
    const { value } = filters;
    return (_jsxs("div", { className: "flex flex-wrap items-end gap-2", "data-testid": "media-filters", children: [withAccount ? (_jsxs("div", { className: "flex flex-col gap-1", children: [_jsx(Label, { htmlFor: "media-account", children: "\u041A\u0430\u0431\u0438\u043D\u0435\u0442" }), _jsx(SearchableSelect, { id: "media-account", className: "w-56", value: value.account ?? '', onChange: filters.setAccount, placeholder: "\u0412\u044B\u0431\u0435\u0440\u0438\u0442\u0435 \u043A\u0430\u0431\u0438\u043D\u0435\u0442", options: accounts.map((a) => ({ value: a.id, label: a.name, keywords: [a.id] })) })] })) : null, _jsxs("div", { className: "flex min-w-48 flex-1 flex-col gap-1", children: [_jsx(Label, { htmlFor: "media-q", children: "\u041F\u043E\u0438\u0441\u043A" }), _jsx(Input, { id: "media-q", type: "search", value: value.q, maxLength: 120, placeholder: "\u041D\u0430\u0437\u0432\u0430\u043D\u0438\u0435 \u0444\u0430\u0439\u043B\u0430 \u0438\u043B\u0438 \u0442\u0435\u043A\u0441\u0442", onChange: (e) => filters.setQ(e.target.value) })] }), _jsxs("div", { className: "flex flex-col gap-1", children: [_jsx(Label, { htmlFor: "media-kind", children: "\u0422\u0438\u043F" }), _jsxs(Select, { value: value.kind ?? ALL, onValueChange: (v) => filters.setKind(v === ALL ? null : v), children: [_jsx(Select.Trigger, { id: "media-kind", className: "w-40", children: _jsx(Select.Value, {}) }), _jsxs(Select.Content, { children: [_jsx(Select.Item, { value: ALL, children: "\u0412\u0441\u0435" }), _jsx(Select.Item, { value: "image", children: "\u041A\u0430\u0440\u0442\u0438\u043D\u043A\u0438" }), _jsx(Select.Item, { value: "video", children: "\u0412\u0438\u0434\u0435\u043E" })] })] })] })] }));
}
/**
 * Прежний ключ `useMediaSource` — тот же источник, кабинет и кабинет запуска
 * (отличаются только поиск / тип): его страницы можно держать до нового
 * ответа. Другой кабинет запуска меняет ответ «Уже в рекламе» (ссылка
 * креатива есть только у своего кабинета) — прежние плитки не держим.
 */
export function sameMediaScope(prevKey, source, platformAccountId, forPlatformAccountId) {
    const scope = prevKey[4];
    return (prevKey[3] === source &&
        scope?.platformAccountId === platformAccountId &&
        scope?.forPlatformAccountId === forPlatformAccountId);
}
/** Страницы источника по курсору; поиск — с паузой 300 мс. */
export function useMediaSource(source, f, enabled = true, 
/** Кабинет запуска: «Уже в рекламе» другого кабинета придёт без ссылки (v4 D10). */
forAccountId = null) {
    const q = useDebouncedValue(f.q.trim(), 300);
    // «Загруженные» переносятся в любой кабинет сами — по кабинету их не фильтруем
    const platformAccountId = source === 'uploaded' ? null : f.account;
    const forPlatformAccountId = source === 'in-ads' ? forAccountId : null;
    return useInfiniteQuery({
        ...ADS_QUERY_DEFAULTS,
        queryKey: [
            ...MEDIA_KEY,
            'sources',
            source,
            { platformAccountId, q, kind: f.kind, forPlatformAccountId },
        ],
        queryFn: ({ pageParam }) => listMediaSources({
            source,
            limit: MEDIA_SOURCE_PAGE,
            ...(platformAccountId ? { platformAccountId } : {}),
            ...(forPlatformAccountId ? { forPlatformAccountId } : {}),
            ...(q ? { q } : {}),
            ...(f.kind ? { kind: f.kind } : {}),
            ...(pageParam ? { cursor: pageParam } : {}),
        }),
        initialPageParam: undefined,
        getNextPageParam: (last) => last.nextCursor ?? undefined,
        enabled: enabled && (source === 'uploaded' || platformAccountId != null),
        // «Уже в рекламе» и «Библиотека кабинета» читают Meta и меняются редко;
        // «Загруженные» — наши, после загрузки сбрасываются инвалидацией `MEDIA_KEY`
        ...(source === 'uploaded' ? {} : { staleTime: REFERENCE_STALE_MS }),
        // смена поиска/типа не мигает пустой сеткой — прежние плитки до ответа; но
        // другой источник, кабинет или кабинет запуска — только свои плитки (чужую не выбрать)
        placeholderData: (prev, prevQuery) => prevQuery !== undefined &&
            sameMediaScope(prevQuery.queryKey, source, platformAccountId, forPlatformAccountId)
            ? prev
            : undefined,
    });
}
/**
 * «Видео · 1080×1920 · 14 с» / «Картинка · 1080×1080»: разрешение и
 * длительность — всё, что известно (у видео без данных — просто «Видео»).
 */
export function mediaFacts(m) {
    const parts = [m.kind === 'video' ? 'Видео' : 'Картинка'];
    if (m.width != null && m.height != null)
        parts.push(`${m.width}×${m.height}`);
    if (m.durationSec != null)
        parts.push(formatDurationSec(m.durationSec));
    return parts.join(' · ');
}
/**
 * Плитка файла: превью, имя, факты, где уже есть; у «Уже в рекламе» — текст
 * креатива. В диалоге выбора файл выбирается кликом в любом месте карточки —
 * по превью, названию, текст креатива (v5 N4), как строка «Топ-постов», — а не
 * только маленьким флажком. Флажок остаётся доступным элементом (клавиатура,
 * скринридер); его клик не всплывает к карточке, чтобы не было двойного
 * переключения.
 */
export function MediaSourceTile({ item, accountNames, checked, onCheckedChange, }) {
    const ready = item.inAccounts.filter((a) => a.state === 'ready');
    const selectable = onCheckedChange !== undefined;
    return (_jsxs("li", { className: cn('flex flex-col gap-2 rounded-[var(--radius-2)] border border-[var(--color-border-subtle)] p-2', selectable && 'cursor-pointer select-none'), "data-testid": `media-item-${item.assetId}`, "data-checked": selectable ? String(Boolean(checked)) : undefined, onClick: selectable ? () => onCheckedChange(!checked) : undefined, children: [_jsxs("div", { className: "relative", "data-testid": selectable ? 'media-pick-area' : undefined, children: [_jsx(MediaPreview, { previewUrl: item.previewUrl, kind: item.kind, label: item.name, size: "tile" }), selectable ? (_jsx(Checkbox, { "aria-label": `Выбрать «${item.name}»`, className: "absolute left-2 top-2 bg-[var(--color-surface-raised)]", checked: Boolean(checked), onClick: (e) => e.stopPropagation(), onCheckedChange: (v) => onCheckedChange(v === true) })) : null] }), _jsxs("div", { className: "flex min-w-0 flex-col gap-0.5", children: [_jsx("span", { className: "truncate text-sm font-medium text-[var(--color-text-primary)]", title: item.name, children: item.name }), _jsx("span", { className: "text-xs text-[var(--color-text-muted)]", children: mediaFacts(item) }), item.usage ? (_jsx("span", { className: "text-xs text-[var(--color-text-muted)]", children: formatCount(item.usage.creatives, ['объявление', 'объявления', 'объявлений']) })) : null, item.creative?.primaryText ? (_jsx("span", { className: "line-clamp-2 text-xs text-[var(--color-text-secondary)]", children: item.creative.primaryText })) : null, ready.length > 0 ? (_jsx("div", { className: "flex flex-wrap gap-1", children: ready.map((a) => (_jsx(Badge, { variant: "neutral", children: accountNames.get(a.platformAccountId) ?? a.platformAccountId }, a.platformAccountId))) })) : null] })] }));
}
const EMPTY = {
    uploaded: {
        title: 'Загруженных файлов нет',
        description: 'Перетащите картинку или видео в поле выше.',
    },
    'in-ads': {
        title: 'В рекламе кабинета файлов нет',
        description: 'Здесь появятся картинки и видео объявлений этого кабинета.',
    },
    'account-library': {
        title: 'В библиотеке кабинета нет новых файлов',
        description: 'Файлы, которые уже были в рекламе, — во вкладке «Уже в рекламе».',
    },
};
/**
 * Сетка источника с бесконечной подгрузкой. `isPicked`/`onToggle` — выбор
 * (диалог); без них — просмотр (страница «Медиатека»).
 */
export function MediaSourceGrid({ source, filters, accountNames, isPicked, onToggle, enabled = true, forAccountId = null, }) {
    const q = useMediaSource(source, filters, enabled, forAccountId);
    const items = q.data?.pages.flatMap((p) => p.items) ?? [];
    const needsAccount = source !== 'uploaded' && filters.account == null;
    return (_jsxs("div", { className: "flex flex-col gap-3", children: [_jsx(PickerResults, { error: q.isError ? q.error : null, loading: q.isLoading, empty: needsAccount || items.length === 0, emptyState: {
                    icon: Images,
                    ...(needsAccount
                        ? { title: 'Выберите кабинет', description: 'Файлы показываются по одному кабинету.' }
                        : filters.q.trim() || filters.kind
                            ? { title: 'Ничего не нашлось', description: 'Измените поиск или тип.' }
                            : EMPTY[source]),
                }, children: _jsx("ul", { className: "grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4", "data-testid": `media-grid-${source}`, children: items.map((item) => (_jsx(MediaSourceTile, { item: item, accountNames: accountNames, ...(onToggle
                            ? {
                                checked: isPicked?.(item) ?? false,
                                onCheckedChange: (on) => onToggle(item, on),
                            }
                            : {}) }, item.assetId))) }) }), q.data && !needsAccount ? (_jsx(LoadMoreFooter, { autoLoad: true, hasNextPage: q.hasNextPage, isFetchingNextPage: q.isFetchingNextPage, onLoadMore: () => void q.fetchNextPage(), shown: items.length, allShownLabel: (n) => `Показаны все файлы (${n})` })) : null] }));
}
//# sourceMappingURL=media-sources.js.map